import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Box, Button, IconButton, TextField } from "@mui/material";
import { InfoOutlined, ModeEditOutlineTwoTone, Add } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import CustomTabs from "components/Shared/CustomTabs";
import OpenInNewTab from "components/Shared/OpenInNewTab";
import TableFilter from "components/Shared/AppTable/TableFilter";
import ParamsUserAutocomplete from "components/Shared/ParamsUserAutocomplete";
import HighlightedText from "components/Shared/HighlightedText";

import { useLazyGetTicketListQuery } from "store/services/ticketApi";

import { useAuth, useAppMeta, useTablePagination } from "hooks";
import ExportTicketExcel from "./ticketComponents/ExportTicketExcel";
import BaseSelect from "components/Shared/Base/BaseSelect";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useLazyGetDdlVendorProfileQuery } from "store/services/vendorProfileApi";
import { useCustomParams } from "hooks/useCustomParams";
import { useListListQuery } from "store/services/listService";
import { ENTITY_LIST_CONSTANTS } from "data/static";

const TicketListScreen = () => {
  const { isEntityAdmin, isVendor, isVendorStaff, viewEntity } = useAuth();

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();
  const { appendParams } = useCustomParams();

  const activeTab = searchParams.get("tab") || "Open";
  const keyword = searchParams.get("search") || "";
  const responsible = searchParams.get("responsible") || null;
  const ticketType = searchParams.get("ticketType") || "All";
  const vendorId = searchParams.get("vendorId") || null;
  const timeRange = searchParams.get("timeRange") || "all";
  const fromDate = searchParams.get("fromDate") || "";
  const toDate = searchParams.get("toDate") || "";

  const handleDateRangeChange = (value) => {
    if (value === "last7days") {
      appendParams({
        timeRange: value,
        fromDate: moment().subtract(6, "days").format("YYYY-MM-DD"),
        toDate: moment().format("YYYY-MM-DD"),
      });
    } else if (value === "last30days") {
      appendParams({
        timeRange: value,
        fromDate: moment().subtract(29, "days").format("YYYY-MM-DD"),
        toDate: moment().format("YYYY-MM-DD"),
      });
    } else {
      appendParams({ timeRange: value, fromDate: "", toDate: "" });
    }
  };

  const breadcrumbs = [{ title: "Tickets" }];

  // RTK Query - Main data fetching
  const [
    fetchTickets,
    {
      data: ticketRes,
      isLoading: loadingTickets,
      isFetching: fetchingTickets,
      error,
    },
  ] = useLazyGetTicketListQuery();

  // RTK Query - Separate instances for tab counts to avoid interference
  const [fetchOpenCount] = useLazyGetTicketListQuery();
  const [fetchInProgressCount] = useLazyGetTicketListQuery();
  const [fetchVendors, { data: vendors = [], isLoading: loadingVendors }] =
    useLazyGetDdlVendorProfileQuery();

  useEffect(() => {
    if (viewEntity?.entity?._id && viewEntity?.entity?.account?._id && !isVendor) {
      fetchVendors({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
    }
  }, [fetchVendors, isVendor, viewEntity?.entity?._id, viewEntity?.entity?.account?._id]);

  const selectedVendor = useMemo(
    () => vendors.find((vendor) => vendor?._id === vendorId) || null,
    [vendors, vendorId]
  );

  // State to store badge counts for tabs
  const [tabCounts, setTabCounts] = useState({
    Open: 0,
    InProgress: 0,
    DueDate: 0,
  });

  const {
    data: ticketTypes = [],
    isLoading: isLoadingTicketTypes,
    isFetching: isFetchingTicketTypes,
  } = useListListQuery(
    {
      type: ENTITY_LIST_CONSTANTS.TICKET_TYPE,
      status: "Active",
    },
    { skip: false, refetchOnMountOrArgChange: true }
  );

  const _mappedTicketTypes = useMemo(
    () => [
      { label: "All", _id: "All" },
      ...(Array.isArray(ticketTypes)
        ? ticketTypes.map((item) => ({
          label: item?.name || "",
          _id: item?._id || "",
        }))
        : []),
    ],
    [ticketTypes]
  );

  const isDueDateTab = activeTab === "Due Date";

  // Fetch main ticket data based on active tab and filters
  useEffect(() => {
    fetchTickets({
      active: activeTab !== "Inactive",
      ...(!["All", "Inactive", "Due Date"].includes(activeTab) && {
        stage: activeTab,
      }),
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      ...(responsible && { responsible }),
      ...(ticketType && { ticketType }),
      ...(vendorId && { vendorId }),
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
      ...(isDueDateTab && { overdue: "true" }),
    });
  }, [
    activeTab,
    currentPage,
    perPage,
    fetchTickets,
    responsible,
    keyword,
    ticketType,
    vendorId,
    fromDate,
    toDate,
  ]);

  // Fetch counts for Open and In Progress tabs separately
  useEffect(() => {
    const fetchTabCounts = async () => {
      try {
        // Create base filter params
        const baseParams = {
          active: true,
          page: 1,
          limit: 1, // Minimal data to get totalItems only
          ...(keyword && { keyword }),
          ...(responsible && { responsible }),
          ...(ticketType && ticketType !== "All" && { ticketType }),
          ...(vendorId && { vendorId }),
          ...(fromDate && { fromDate }),
          ...(toDate && { toDate }),
        };

        // Fetch both counts concurrently but separately
        const [
          openResponse,
          inProgressResponse,
          closedResponse,
          deadlineResponse,
        ] = await Promise.all([
          fetchOpenCount({
            ...baseParams,
            stage: "Open",
          }).unwrap(),
          fetchInProgressCount({
            ...baseParams,
            stage: "In Progress",
          }).unwrap(),
          fetchInProgressCount({
            ...baseParams,
            stage: "Closed",
          }).unwrap(),
          fetchInProgressCount({
            ...baseParams,
            overdue: "true",
          }).unwrap(),
        ]);

        setTabCounts({
          Open: openResponse?.totalItems || 0,
          InProgress: inProgressResponse?.totalItems || 0,
          Closed: closedResponse?.totalItems || 0,
          DueDate: deadlineResponse?.totalItems || 0,
        });
      } catch (err) {
        // Set to 0 on error to avoid showing incorrect counts
        setTabCounts({
          Open: 0,
          InProgress: 0,
          Closed: 0,
          DueDate: 0,
        });
      }
    };

    // Only fetch tab counts, don't interfere with main data
    fetchTabCounts();
  }, [keyword, responsible, ticketType, vendorId, fromDate, toDate, fetchOpenCount, fetchInProgressCount]);

  const ticketTabs = useMemo(
    () => [
      {
        label: "Open",
        value: "Open",
        badgeCount: tabCounts.Open,
      },
      {
        label: "In Progress",
        value: "In Progress",
        badgeCount: tabCounts.InProgress,
      },
      {
        label: "Closed",
        value: "Closed",
      },
      {
        label: "All",
        value: "All",
      },
      ...(!isVendor && !isVendorStaff
        ? [
          {
            label: "Due Date",
            value: "Due Date",
            badgeCount: tabCounts.DueDate,
          },
        ]
        : []),
      {
        label: "Inactive",
        value: "Inactive",
      },
    ],
    [tabCounts.InProgress, tabCounts.Open, tabCounts.DueDate, isVendor, isVendorStaff]
  );

  const columns = [
    {
      name: "Subject",
      cell: (row) => (
        <Link
          to={
            isVendor
              ? `/app/vendor/ticket/${row?._id}/detail?prevTab=${activeTab}`
              : isVendorStaff
                ? `/app/staff/ticket/${row?._id}/detail?prevTab=${activeTab}`
                : `/app/crm/ticket/${row?._id}/detail?prevTab=${activeTab}`
          }
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          title={row?.subject}
        >
          <span className="text-blue-500 dark:text-blue-200">
            #{row?.ticketId}
          </span>{" "}
          <HighlightedText text={row?.subject} highlight={keyword} />
        </Link>
      ),
      minWidth: "250px",
    },
    ...(!isEntityAdmin && !isVendor
      ? [
        {
          name: "Entity",
          cell: (row) => (
            <div
              title={row?.entity?.displayName ?? row?.entity?.name}
              className="line-clamp-2"
            >
              {row?.entity?.displayName ?? row?.entity?.name}
            </div>
          ),
        },
      ]
      : []),
    {
      name: "Source",
      cell: (row) =>
        row?.sourceRef ? (
          isVendor ? (
            <div className="flex flex-col gap-1">
              <div> {row?.sourceRef?.shopName}</div>
              <div className="text-xs font-medium text-blue-500">
                {row?.source && row.source.replace(/([a-z])([A-Z])/g, "$1 $2")}
              </div>
            </div>
          ) : row?.source === "Customer" ? (
            <div className="flex flex-col gap-1">
              <div title={row?.sourceRef?.name} className="line-clamp-2">
                {row?.sourceRef?.name ?? "N/A"}
              </div>
              <div className="text-xs font-medium text-blue-500">Customer</div>
            </div>
          ) : (
            <OpenInNewTab
              label={row?.sourceRef?.shopName}
              link={`/app/crm/vendor-profile/${row?.sourceRef?._id}/detail`}
              disableIcon
              disableColor
              disableHover
            >
              <div className="flex flex-col gap-1">
                <div> {row?.sourceRef?.shopName}</div>
                <div className="text-xs font-medium text-blue-500">
                  {row?.source === "VendorProfile" && "Vendor Profile"}
                </div>
              </div>
            </OpenInNewTab>
          )
        ) : (
          "N/A"
        ),
    },
    {
      name: "Type",
      cell: (row) => (
        <div title={row?.ticketType?.name} className="line-clamp-2">
          {row?.ticketType?.name ?? "N/A"}
        </div>
      ),
    },

    ...(["All", "Due Date"].includes(activeTab)
      ? [
        {
          name: "Stage",
          selector: (row) => row?.stage,
        },
      ]
      : []),
    ...(isDueDateTab
      ? [
        {
          name: "Due Date",
          cell: (row) => <AppTableDateTime date={row?.dueDate} />,
        },
      ]
      : []),
    {
      name: "Created At",
      cell: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    ...(activeTab === "Closed"
      ? [
        {
          name: "Closed At",
          cell: (row) => (
            <div className="grid grid-cols-1 gap-1 py-1">
              <AppTableDateTime date={row?.closedAt} />
              {row?.closedBy && (
                <div className="text-sm text-gray-500 dark:text-gray-200">
                  by {row?.closedBy?.name}
                </div>
              )}
            </div>
          ),
        },
      ]
      : []),
    {
      name: "Responsible",
      cell: (row) => (
        <div title={row?.responsible?.name} className="line-clamp-2">
          {row?.responsible?.name ?? "N/A"}
        </div>
      ),
    },
    {
      name: "Created By",
      cell: (row) => (
        <div title={row?.createdBy?.name} className="line-clamp-2">
          {row?.createdBy?.name ?? "N/A"}
        </div>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <Box display="flex" alignItems="center">
          <Link
            to={
              isVendor
                ? `/app/vendor/ticket/${row?._id}/detail?prevTab=${activeTab}`
                : isVendorStaff
                  ? `/app/staff/ticket/${row?._id}/detail?prevTab=${activeTab}`
                  : `/app/crm/ticket/${row?._id}/detail?prevTab=${activeTab}`
            }
          >
            <IconButton size="small" aria-label="Ticket">
              <InfoOutlined color="primary" />
            </IconButton>
          </Link>
          <Link
            to={
              isVendor
                ? `/app/vendor/ticket/${row?._id}/edit`
                : isVendorStaff
                  ? `/app/staff/ticket/${row?._id}/edit`
                  : `/app/crm/ticket/${row?._id}/edit`
            }
          >
            <IconButton size="small" aria-label="Edit Ticket">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </Box>
      ),
      ignoreRowClick: true,
      button: true,
    },
  ];

  useAppMeta(`${activeTab} Tickets`);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <BreadcrumbLayout
        breadcrumbs={breadcrumbs}
        headerActions={
          <PageActions
            isBusy={loadingTickets || fetchingTickets}
            isVendor={isVendor}
            isVendorStaff={isVendorStaff}
          />
        }
      >
        <BreadcrumbLayout.Error error={error} />

        <BreadcrumbLayout.Paper>
          <CustomTabs tabs={ticketTabs} activeTab={activeTab} />

          <TableFilter
            filterMineKey={!isVendor && "Responsible"}
            searchLabel="Subject"
            disabled={loadingTickets || fetchingTickets}
            actions={
              <ExportTicketExcel
                disabled={loadingTickets || fetchingTickets}
                filters={{
                  active: activeTab !== "Inactive",
                  ...(!["All", "Inactive", "Due Date"].includes(activeTab) && {
                    stage: activeTab,
                  }),
                  ...(keyword && { keyword }),
                  ...(responsible && { responsible }),
                  ...(isDueDateTab && { overdue: "true" }),
                  ...(ticketType && ticketType !== "All" && { ticketType }),
                  ...(vendorId && { vendorId }),
                  ...(fromDate && { fromDate }),
                  ...(toDate && { toDate }),
                }}
              />
            }
          >
            {!isVendor && (
              <ParamsUserAutocomplete
                label="Responsible"
                disabled={loadingTickets || fetchingTickets}
              />
            )}

            <BaseSelect
              fullWidth
              label="Type"
              value={ticketType}
              onChange={(e) => {
                appendParams({
                  ticketType: e.target.value,
                });
              }}
              options={_mappedTicketTypes}
              isLoading={isLoadingTicketTypes || isFetchingTicketTypes}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt._id}
            />

            {!isVendor && (
              <BaseAutocomplete
                fullWidth
                label="Vendor"
                value={selectedVendor}
                options={vendors}
                loading={loadingVendors}
                onChange={(_, selected) =>
                  appendParams({ vendorId: selected?._id || null })
                }
                getOptionLabel={(option) =>
                  option ? `${option.ownerName || ""} - ${option.shopName || ""}` : ""
                }
                isOptionEqualToValue={(option, value) => option?._id === value?._id}
              />
            )}

            <BaseSelect
              fullWidth
              label="Date Range"
              value={timeRange}
              onChange={(e) => handleDateRangeChange(e.target.value)}
              options={[
                { label: "All Dates", value: "all" },
                { label: "Last 7 Days", value: "last7days" },
                { label: "Last 30 Days", value: "last30days" },
                { label: "Custom", value: "custom" },
              ]}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.value}
            />

            {timeRange === "custom" && (
              <>
                <DatePicker
                  label="From Date"
                  value={fromDate ? moment(fromDate) : null}
                  onChange={(value) =>
                    appendParams({
                      fromDate: value ? value.format("YYYY-MM-DD") : "",
                    })
                  }
                  renderInput={(props) => <TextField {...props} fullWidth size="small" />}
                  maxDate={toDate ? moment(toDate) : undefined}
                />
                <DatePicker
                  label="To Date"
                  value={toDate ? moment(toDate) : null}
                  onChange={(value) =>
                    appendParams({
                      toDate: value ? value.format("YYYY-MM-DD") : "",
                    })
                  }
                  renderInput={(props) => <TextField {...props} fullWidth size="small" />}
                  minDate={fromDate ? moment(fromDate) : undefined}
                  maxDate={moment()}
                />
              </>
            )}
          </TableFilter>

          <AppTable
            columns={columns}
            data={ticketRes?.data ?? []}
            progressPending={loadingTickets || fetchingTickets}
            paginationServer
            paginationPerPage={perPage}
            paginationTotalRows={ticketRes?.totalItems ?? 0}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
          />
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </LocalizationProvider>
  );
};

export default TicketListScreen;

const PageActions = ({ isVendor, isVendorStaff }) => {
  return (
    <div className="flex space-x-4">
      <Link
        to={
          isVendor
            ? "/app/vendor/ticket/create"
            : isVendorStaff
              ? "/app/staff/ticket/create"
              : "/app/crm/ticket/create"
        }
      >
        <Button variant="contained" startIcon={<Add />}>
          New Ticket
        </Button>
      </Link>
    </div>
  );
};
